import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import useBlobs from "@/common/hooks/queries/useBlobs";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { FormTransformers } from "@/common/utils/formTransformers";
import useRemoveEstablishmentCompanyBranchImage from "@/features/establishments/hooks/useRemoveEstablishmentCompanyBranchImage";
import { useUpdateEstablishmentCompany } from "@/features/establishments/hooks/useUpdateEstablishmentCompany";
import useUpdateEstablishmentCompanyBranchImage from "@/features/establishments/hooks/useUpdateEstablishmentCompanyBranchImage";
import { updateEstablishmentCompanySchema, UpdateEstablishmentCompanyValidationSchema } from "@/features/establishments/schemas/updateEstablishmentCompany.schema";
import { updateEstablishmentCompanyDefaultValues } from "@/features/establishments/utils/updateEstablishmentCompanyDefaultValues";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface UpdateEstablishmentCaModuleProps {
  establishmentCompany?: EstablishmentCompanyData;
  establishmentCompanyIsLoading?: boolean;
  disabled?: boolean;
}

const UpdateAccreditationInformation = ({ establishmentCompany, establishmentCompanyIsLoading, disabled }: UpdateEstablishmentCaModuleProps) => {
  const queryClient = useQueryClient();

  const { hasUserPermission } = useUserPermissions();

  const canEditImages = hasUserPermission("Admin::Establishment::CompanyBranches::Update");

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [imageMutationIsPending, setImageMutationIsPending] = useState(false);

  const {
    mutateAsync: uploadBlob,
  } = useBlobs();

  const {
    mutateAsync: removeImage,
  } = useRemoveEstablishmentCompanyBranchImage({
    establishmentCompanyBranchId: establishmentCompany?.matrix_branch_id,
  });

  const {
    mutateAsync: updateImage,
  } = useUpdateEstablishmentCompanyBranchImage({
    establishmentCompanyBranchId: establishmentCompany?.matrix_branch_id,
  });

  const handleUpload = async ({ file, filename }: { file: File; filename: string }) => {
    setImageMutationIsPending(true);

    const response = await uploadBlob({ file, filename });

    const blobId = response?.data?.data?.id;

    if (blobId) {
      await updateImage({ blobId }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['establishmentCompany'] });
        },
        onError: FormTransformers.errorsTransformer({ setError }),
      });
    }

    setImageMutationIsPending(false);

    return response
  }

  const handleRemove = async (photoId: string) => {
    setImageMutationIsPending(true);

    await removeImage(photoId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['establishmentCompany'] });

        setImageMutationIsPending(false);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setMessages,
        })(error);

        setImageMutationIsPending(false);
      },
    });
  }

  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    control,
    setError,
    formState: {
      errors
    },
  } = useForm<UpdateEstablishmentCompanyValidationSchema>({
    resolver: zodResolver(updateEstablishmentCompanySchema),
    defaultValues: updateEstablishmentCompanyDefaultValues(establishmentCompany),
  });

  const {
    mutate: updateEstablishmentCompany,
    isPending: updateEstablishmentCompanyIsPending,
  } = useUpdateEstablishmentCompany({
    establishmentCompanyId: establishmentCompany?.id,
    establishmentCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] })
      setIsOpen(false);
    },
    onError: (error) => {
      setIsOpen(false);

      reset(updateEstablishmentCompanyDefaultValues(establishmentCompany));

      FormTransformers.errorsTransformer({
        setMessages,
        setError,
      })(error);
    },
  });

  const onSubmit: SubmitHandler<UpdateEstablishmentCompanyValidationSchema> = (data) => updateEstablishmentCompany(data);

  useEffect(() => {
    if (!establishmentCompanyIsLoading) {
      reset(updateEstablishmentCompanyDefaultValues(establishmentCompany));
    }
  }, [establishmentCompanyIsLoading, isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerDisabled={disabled}
        Trigger={
          <Button operationKlassToCheckPermission="Admin::Establishment::Companies::Update" disabled={disabled}>
            Editar
          </Button>
        }
        useCancellableFooter
        showClose={false}
        title="Editar dados de credenciamento"
        className="min-w-[720px]"
        bodyClassName="text-white-800"
        titleClass="text-2xl"
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        disabled={updateEstablishmentCompanyIsPending}
        confirmButtonIsLoading={updateEstablishmentCompanyIsPending}
        onCancel={reset}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form>
            <div className="flex items-start gap-4">
              <div className="flex flex-col gap-4 flex-1">
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs">
                    Sistemas habilitados
                  </p>

                  <div className="flex flex-col gap-3">
                    <Checkbox
                      checked={!!watch('ca_module_enabled')}
                      label="Controle de abastecimento (CA)"
                      onChange={(event) => setValue('ca_module_enabled', event.currentTarget.checked)}
                    />

                    <Checkbox
                      checked={!!watch('cm_module_enabled')}
                      label="Controle de manutenção (CM)"
                      onChange={(event) => setValue('cm_module_enabled', event.currentTarget.checked)}
                    />

                    <Checkbox
                      checked={!!watch('tmais_module_enabled')}
                      label="Taurus + (T+)"
                      onChange={(event) => setValue('tmais_module_enabled', event.currentTarget.checked)}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <p className="text-xs">
                    Realiza coleta/guincho de equipamento?
                  </p>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      type="radio"
                      name="tow_service_available"
                      label="Sim"
                      className="items-center gap-1.5"
                      boxClassName="rounded-full size-6"
                      checked={!!watch?.('tow_service_available')}
                      onChange={(event) => {
                        if (event.currentTarget.checked) {
                          setValue('tow_service_available', true);
                        }
                      }}
                    />

                    <Checkbox
                      type="radio"
                      name="tow_service_available"
                      label="Não"
                      className="items-center gap-1.5"
                      boxClassName="rounded-full size-6"
                      checked={!watch?.('tow_service_available')}
                      onChange={(event) => {
                        if (event.currentTarget.checked) {
                          setValue('tow_service_available', false);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <MultipleImageUploader
                  label="Anexe uma foto"
                  name="photos"
                  uploadButtonLayout="floating"
                  accept="image/jpeg, image/png"
                  uploadBlob={handleUpload}
                  control={control}
                  fieldName="photos"
                  isLoading={imageMutationIsPending}
                  disabled={imageMutationIsPending}
                  hasPermission={canEditImages}
                  onRemove={handleRemove}
                  errorMessage={errors?.photos?.message || undefined}
                />
              </div>
            </div>
          </form>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
};

export default UpdateAccreditationInformation;
